/**
 * <product-accordions> — animated <details> accordions for product information.
 *
 * Native <details> stays the source of truth (open-by-default works server-side and
 * with no JS). This element intercepts the toggle and animates the content height
 * with the Web Animations API (measuring scrollHeight), so it works across all
 * evergreen browsers. It self-initialises on connectedCallback, so it survives the
 * product-info DOM morph; a11y wiring lives here (not the one-time global.js shim).
 */
if (!customElements.get('product-accordions')) {
  const OPEN_MS = 280;
  const CLOSE_MS = 240;

  class ProductAccordions extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items = Array.from(this.querySelectorAll('details.product-accordion'));
      this.items.forEach((details) => this.setup(details));
    }

    setup(details) {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.product-accordion__content');
      if (!summary || !content) return;

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (details.dataset.animating) return;
        if (details.open) {
          this.collapse(details, content, summary);
        } else {
          this.expand(details, content, summary);
        }
      });
    }

    expand(details, content, summary) {
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');

      if (this.reduceMotion) return;

      const target = content.scrollHeight;
      details.dataset.animating = '1';
      const animation = content.animate(
        { height: ['0px', `${target}px`], opacity: [0, 1] },
        { duration: OPEN_MS, easing: 'ease' }
      );
      animation.onfinish = animation.oncancel = () => {
        content.style.height = '';
        delete details.dataset.animating;
      };
    }

    collapse(details, content, summary) {
      summary.setAttribute('aria-expanded', 'false');

      if (this.reduceMotion) {
        details.open = false;
        return;
      }

      const start = content.scrollHeight;
      details.dataset.animating = '1';
      const animation = content.animate(
        { height: [`${start}px`, '0px'], opacity: [1, 0] },
        { duration: CLOSE_MS, easing: 'ease' }
      );
      animation.onfinish = animation.oncancel = () => {
        details.open = false;
        content.style.height = '';
        delete details.dataset.animating;
      };
    }
  }

  customElements.define('product-accordions', ProductAccordions);
}
